import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import InvitePanel from "@/components/studio/InvitePanel";
import { toast } from "sonner";
import { ArrowLeft, Eye, Image as ImageIcon, Loader2, Plus, Save, Users } from "lucide-react";
import { downloadPng } from "@/lib/media";
import BFPackageEditor from "./BFPackageEditor";
import BFStoryCard from "./BFStoryCard";
import BFLeads from "./BFLeads";
import BFProformas from "./BFProformas";
import BFWaits from "./BFWaits";
import BFMySalons from "./BFMySalons";
import BFUnassigned from "./BFUnassigned";
import BFMoves from "./BFMoves";
import BFRebuild from "./BFRebuild";
import BFBilling from "./BFBilling";
import BFMissingCodes from "./BFMissingCodes";
import { findDups } from "@/lib/bfDups";
import { BFSectionHead, BFTiles, SECTIONS, SUBS } from "./BFTiles";
import BFDocs from "./BFDocs";
import BFContractsMissing from "./BFContractsMissing";
import BFContractsArea from "./BFContractsArea";
import BFContractSeries from "./BFContractSeries";
import BFPendingOrders from "./BFPendingOrders";
import BFGamaEditor from "./BFGamaEditor";
import BFEventsAdmin from "./BFEventsAdmin";
import BFInterested from "./BFInterested";
import BFSalonMove from "./BFSalonMove";
import BFAdminPlan from "./BFAdminPlan";
import BFListShare from "./BFListShare";
import BFAgentsWA from "./BFAgentsWA";
import { fetchUnassigned } from "@/lib/bf";
import BFAppTabs from "./BFAppTabs";
import BFFreeOrder from "./BFFreeOrder";
import BFRegRank from "./BFRegRank";
import BFReminder from "./BFReminder";
import BFCatalogs from "./BFCatalogs";
import BFCampaignEditor from "./BFCampaignEditor";
import Field from "@/components/studio/Field";
import { bfPublicUrl, campaignState, fetchBFAdmin, fetchBFStats, logo, ron, saveBFAdmin } from "@/lib/bf";
import BFProductRow, { num } from "./BFProductRow";

const SETTINGS = [
  ["title", "Titlu"], ["subtitle", "Subtitlu"], ["intro", "Text scurt"],
  ["whatsapp", "WhatsApp pentru comenzi"], ["contact", "Nume afișat în subsol"],
  ["op_name", "GDPR · firma operator"], ["op_cui", "GDPR · CUI operator"], ["op_address", "GDPR · sediul operatorului"], ["op_email", "GDPR · e-mail pentru cereri"],
];

const Stats = ({ s, brands }) => (
  <div className="bfa-stats" data-testid="bfa-stats">
    <span><b data-testid="bfa-stat-visitors">{s.visitors}</b> vizitatori</span>
    <span><b>{s.visits}</b> vizite</span>
    <span><b data-testid="bfa-stat-orders">{s.orders}</b> comenzi WhatsApp</span>
    <span><b>{ron(s.orders_total)}</b> valoare coșuri</span>
    {brands.filter((b) => s.brands[b.id]).map((b) => <span key={b.id}>{b.name} <b>{s.brands[b.id]}</b></span>)}
  </div>
);

const CampaignStats = ({ s, cfg }) => {
  const label = (id) => {
    if (id === "buc") return ["Oferte pe bucată", ""];
    const c = cfg.campaigns.find((x) => x.id === id) || cfg.packages.find((k) => `pk:${k.id}` === id);
    const b = cfg.brands.find((x) => x.id === c?.brand)?.name || "";
    return [c ? `${id.startsWith("pk:") ? "Pachet · " : ""}${c.name}` : "(campanie ștearsă)", b];
  };
  const wants = s.wants || {};
  const rows = [...(s.campaigns || []).map((r) => ({ ...r, wants: wants[r.id] || 0 })),
    ...Object.entries(wants).filter(([id]) => !(s.campaigns || []).some((r) => r.id === id)).map(([id, w]) => ({ id, orders: 0, total: 0, wants: w }))];
  const top = rows[0]?.total || 1;
  return (
    <section className="invite-box" data-testid="bfa-camp-stats">
      <h3>Comenzi pe campanie</h3>
      {!rows.length ? <p className="st-hint">Încă nu există comenzi sau cereri „VREAU”. Clasamentul apare după prima cerere.</p> : (
        <ol className="bfa-rank">
          {rows.map((r, i) => {
            const [name, brand] = label(r.id);
            return (
              <li key={r.id} data-testid={`bfa-rank-${r.id}`}>
                <em>{i + 1}</em>
                <div><b>{name}</b><small>{brand}</small><i style={{ width: `${Math.max(4, (r.total / top) * 100)}%` }} /></div>
                <span><b data-testid={`bfa-rank-total-${r.id}`}>{ron(r.total)}</b><small>{r.orders} {r.orders === 1 ? "comandă" : "comenzi"} · <span data-testid={`bfa-rank-wants-${r.id}`}>{r.wants} VREAU</span></small></span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

export default function BFStudio() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("medavita");
  const [busy, setBusy] = useState(false);
  const [story, setStory] = useState(undefined);
  const [pq, setPq] = useState("");
  const [all, setAll] = useState(false);
  const [onlyDup, setOnlyDup] = useState(false);
  const storyRef = useRef(null);
  const [params, setParams] = useSearchParams();
  const ALIAS = { comenzi: "proforme", cataloage: "produse" };
  const s0 = ALIAS[params.get("s")] || params.get("s");
  const sec = SECTIONS.some((x) => x.id === s0) ? s0 : "";
  const open = (id, t) => { setParams(id ? { s: id, ...(t ? { t } : {}) } : {}); window.scrollTo({ top: 0 }); };
  const [unassigned, setUnassigned] = useState(0);
  const [freeIn, setFreeIn] = useState(null);
  useEffect(() => {
    fetchUnassigned().then((d) => setUnassigned(d.count)).catch(() => {});
  }, [sec]);
  const exportStory = async (b) => {
    setStory(b);
    await new Promise((r) => setTimeout(r, 100));
    const node = storyRef.current;
    await Promise.all([...node.querySelectorAll("img")].map((im) => (im.complete ? null : new Promise((r) => { im.onload = r; im.onerror = r; }))));
    try {
      await downloadPng(node, `BlackFriday_${b || "toate"}.png`);
      toast.success("Card descărcat");
    } catch {
      toast.error("Nu am putut genera cardul");
    }
    setStory(undefined);
  };
  useEffect(() => {
    fetchBFAdmin().then(setCfg).catch(() => toast.error("Nu am putut încărca Black Friday"));
    fetchBFStats().then(setStats).catch(() => {});
  }, []);
  if (!cfg) return <div className="st-loading"><Loader2 className="animate-spin" /> Se încarcă…</div>;
  const set = (k) => (v) => setCfg({ ...cfg, [k]: v });
  const setProduct = (np) => setCfg({ ...cfg, products: cfg.products.map((p) => (p.id === np.id ? np : p)) });
  const remove = (id) => setCfg({ ...cfg, products: cfg.products.filter((p) => p.id !== id), campaigns: cfg.campaigns.map((c) => ({ ...c, items: c.items.filter((x) => x !== id), extras: (c.extras || []).filter((x) => x !== id) })), packages: cfg.packages.map((k) => ({ ...k, items: k.items.filter((i) => i.id !== id), gifts: k.gifts.filter((i) => i.id !== id) })) });
  const add = () => setCfg({ ...cfg, products: [...cfg.products, { id: `${tab}${Date.now().toString(36)}`, brand: tab, name: "", image: null, retail: 0, salon: 0, discount: 25, active: true }] });
  const save = () => {
    if (cfg.products.some((p) => !p.name.trim())) return toast.error("Fiecare produs trebuie să aibă un nume");
    if (cfg.end < cfg.start) return toast.error("Data de final e înaintea datei de start");
    setBusy(true);
    const { brands, ...rest } = cfg;
    const body = { ...rest, public_url: window.location.hostname.endsWith("beautytrends.ro") ? bfPublicUrl() : rest.public_url, products: rest.products.map((p) => ({ ...p, retail: num(p.retail), salon: num(p.salon) })) };
    saveBFAdmin(body).then((r) => toast.success(r.notified ? `Black Friday salvat · trimitem anunț la ${r.notified} ${r.notified === 1 ? "salon care aștepta" : "saloane care așteptau"}` : "Black Friday salvat")).catch((e) => { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : Array.isArray(d) && d[0] ? `${d[0].loc?.slice(1).join(" › ")}: ${d[0].msg}` : "Nu am putut salva"); }).finally(() => setBusy(false));
  };
  const dups = findDups(cfg.products.filter((p) => p.brand === tab));
  const dupCount = Object.keys(dups).length;
  const list = cfg.products.filter((p) => p.brand === tab && (!onlyDup || dups[p.id]) && (!pq || `${p.code || ""} ${p.name}`.toLowerCase().includes(pq.toLowerCase())));
  const badges = { saloane: unassigned, produse: cfg.products.filter((p) => p.active && !(p.code || "").trim() && !(p.shades || []).some((s) => (s.code || "").trim())).length };
  const SUB = params.get("t") || (params.get("s") === "cataloage" ? "cataloage" : null);
  const curSub = SUBS[sec] ? (SUBS[sec].some(([t]) => t === SUB) ? SUB : SUBS[sec][0][0]) : "";
  const MAP = { comenzi: ["proforme"], produse: ["produse", "preturi"], cataloage: ["produse", "cataloage"], r_agenti: ["rapoarte", "agenti"], r_saloane: ["rapoarte", "saloane"], r_oferte: ["rapoarte", "oferte"] };
  const is = (id) => { const [s, t] = MAP[id] || [id]; return sec === s && (!t || curSub === t); };
  const brandTabs = (
    <div className="bfa-tabs">
      {cfg.brands.map((b) => (
        <button key={b.id} type="button" className={tab === b.id ? "is-on" : ""} onClick={() => { setTab(b.id); setAll(false); setOnlyDup(false); }} data-testid={`bfa-tab-${b.id}`}>
          <img src={logo(b.id)} alt={b.name} /> <small>{cfg.products.filter((p) => p.brand === b.id).length}</small>
        </button>
      ))}
    </div>
  );
  return (
    <div className="st-app bfa" data-testid="bfa-page">
      <header className="st-top">
        <div className="st-brand">
          <Link to="/" className="st-btn st-btn--ghost" data-testid="bfa-back"><ArrowLeft size={16} /> Catalog</Link>
          <span className="st-brand-mark">Black <em>Friday</em></span>
        </div>
        <div className="st-top-actions">
          <Link to="/utilizatori" className="st-btn st-btn--ghost" data-testid="bfa-users-link"><Users size={16} /> Agenți</Link>
          <a className="st-btn st-btn--ghost" href={`${bfPublicUrl()}?preview=${cfg.preview_token}`} target="_blank" rel="noopener noreferrer" data-testid="bfa-preview"><Eye size={16} /> Vezi pagina</a>
          <button type="button" className="st-btn st-btn--silver" onClick={save} disabled={busy} data-testid="bfa-save">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvează
          </button>
        </div>
      </header>
      <BFAppTabs />
      <main className="users-main">
      {!sec && <BFTiles badges={badges} onOpen={open} />}
      {sec && <BFSectionHead id={sec} sub={curSub} onBack={() => open("")} onOpen={open} />}
      {is("saloane") && <BFUnassigned />}
      {is("produse") && <BFMissingCodes products={cfg.products} brands={cfg.brands} setProduct={setProduct} save={save} busy={busy} />}
      {is("comenzi") && <><BFPendingOrders /><BFProformas /><BFFreeOrder incoming={freeIn} /><BFRebuild onFree={(text) => setFreeIn({ text, n: Date.now() })} /><BFBilling /></>}
      {is("setari") && <section className={`invite-box bfa-publish ${cfg.published ? "is-live" : ""}`} data-testid="bfa-publish-box">
        <label className="bfa-active">
          <input type="checkbox" checked={cfg.published} onChange={(e) => set("published")(e.target.checked)} data-testid="bfa-published-toggle" />
          <b>{cfg.published || campaignState(cfg).phase !== "soon" ? "Publicată: oricine o poate vedea" : `Ascunsă până pe ${cfg.start.split("-").reverse().join(".")} la ${cfg.start_time}: nimeni nu o vede în afară de tine`}</b>
        </label>
        <p className="st-hint">Se publică automat la data și ora lansării (ora României). Agenții invitați mai jos văd deja în contul lor caseta Black Friday, cu linkul personal și cardurile pe brand. Bifează doar dacă vrei să o deschizi mai devreme, apoi apasă Salvează.</p>
        <p className="st-hint">Link public: <b>{bfPublicUrl()}</b></p>
        <p className="st-hint">Link privat de previzualizare, doar pentru tine: <b data-testid="bfa-preview-link">{`${bfPublicUrl()}?preview=${cfg.preview_token}`}</b></p>
      </section>}
      {is("agenti") && <InvitePanel />}
      {is("agenti") && <BFAgentsWA />}
      {is("contracte") && <><BFContractsArea admin /><BFContractsMissing admin /><BFDocs /><BFContractSeries /></>}
      {is("r_oferte") && stats && <CampaignStats s={stats} cfg={cfg} />}
      {is("panou") && stats && <Stats s={stats} brands={cfg.brands} />}
      {is("panou") && stats && <CampaignStats s={stats} cfg={cfg} />}
      {is("r_agenti") && <BFRegRank />}
      {is("r_agenti") && stats?.agents?.length > 0 && (
        <section className="invite-box" data-testid="bfa-agent-stats">
          <h3>Clasament agenți: comenzi WhatsApp aduse de linkul fiecăruia</h3>
          <ol className="bfa-rank">
            {stats.agents.map((a, i) => (
              <li key={a.slug} data-testid={`bfa-agent-${a.slug}`}>
                <em>{i + 1}</em>
                <div><b>{a.name}</b><small>{a.orders} {a.orders === 1 ? "comandă" : "comenzi"} WhatsApp · {a.wants} VREAU · {a.visitors} vizitatori</small>
                {a.brands?.length > 0 && <span className="bfa-agent-brands" data-testid={`bfa-agent-brands-${a.slug}`}>{a.brands.map((b) => <em key={b.brand}>{b.name} <b>{ron(b.total)}</b></em>)}</span>}</div>
                <span><b>{ron(a.total)}</b></span>
              </li>
            ))}
          </ol>
        </section>
      )}
      {is("cataloage") && <BFCatalogs cfg={cfg} />}
      {is("saloane") && <BFLeads cfg={cfg} />}
      {is("saloane") && <section className="invite-box" id="bfa-my-salons" data-testid="bfa-my-salons">
        <h3>Clienții tăi înscriși (fără agent sau prin linkul tău)</h3>
        <p className="st-hint">Saloanele care și-au făcut cont direct pe site, fără linkul unui agent, sunt ale tale. Ordonate de la cel mai nou la cel mai vechi.</p>
        <BFMySalons admin />
      </section>}
      {is("agenti") && <BFMoves />}
      {is("fise") && <BFAdminPlan />}
      {is("educatie") && <><BFEventsAdmin /><BFInterested /></>}
      {is("mutari") && <BFSalonMove />}
      {is("mutari") && <BFMoves />}
      {is("liste") && <section className="invite-box" data-testid="bfa-lists"><BFListShare cfg={cfg} agent={null} url={cfg.public_url || bfPublicUrl()} admin /></section>}
      {is("r_saloane") && <BFReminder cfg={cfg} />}
      {is("setari") && <section className="invite-box">
        <h3>Campanie</h3>
        <div className="bfa-settings">
          {SETTINGS.map(([k, label]) => <Field key={k} label={label} value={cfg[k]} onChange={set(k)} testId={`bfa-${k}-input`} />)}
          <label className="st-field"><span className="st-label">Început</span><input type="date" className="bfa-date" value={cfg.start} onChange={(e) => e.target.value && set("start")(e.target.value)} data-testid="bfa-start-input" /></label>
          <label className="st-field"><span className="st-label">Ora lansării</span><input type="time" className="bfa-date" value={cfg.start_time} onChange={(e) => set("start_time")(e.target.value || "09:00")} data-testid="bfa-start-time-input" /></label>
          <label className="st-field"><span className="st-label">Final</span><input type="date" className="bfa-date" value={cfg.end} onChange={(e) => e.target.value && set("end")(e.target.value)} data-testid="bfa-end-input" /></label>
        </div>
      </section>}
      {is("campanii") && <section className="invite-box" data-testid="bfa-campaigns">
        <h3>Campanii {cfg.brands.find((b) => b.id === tab)?.name}</h3>
        {brandTabs}
        <p className="st-hint">Adaugă întâi produsele brandului în „Produse & prețuri”, apoi bifează-le în campanii. Toate campaniile active apar pe pagină; cele fără produse arată „Produsele se adaugă în curând”.</p>
        <BFCampaignEditor brand={tab} cfg={cfg} setCfg={setCfg} />
      </section>}
      {is("produse") && <BFGamaEditor cfg={cfg} setCfg={setCfg} brand={tab} />}
      {is("produse") && <section className="invite-box">
        <h3>Produse</h3>
        {brandTabs}
        <p className="st-hint">Prețurile de salon sunt precompletate estimativ (60% din prețul de client final de pe luxurybeauty.ro). Verifică-le înainte de lansare. Toate prețurile includ TVA.</p>
        <input className="bfa-name bfa-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Caută produs după cod sau denumire" data-testid="bfa-product-search" />
        {(dupCount > 0 || onlyDup) && <p className="bfa-dup-hint" data-testid="bfa-dup-hint">{dupCount ? `${dupCount} produse active par dubluri (același nume și aceeași mărime sau același cod).` : "Nicio dublură rămasă."} <button type="button" className="bfa-link" onClick={() => setOnlyDup(!onlyDup)} data-testid="bfa-dup-filter">{onlyDup ? "Arată toate produsele" : "Arată doar dublurile"}</button></p>}
        <ul className="bfa-list">
          {(pq || all || onlyDup ? list : list.filter((p, i) => i < 40 || !p.name.trim())).map((p) => <BFProductRow key={p.id} p={p} dups={dups[p.id]} offer={cfg.campaigns.find((c) => c.brand === p.brand && c.category === "oferta" && c.active)} onChange={setProduct} onDelete={() => remove(p.id)} />)}
        </ul>
        {!pq && !all && !onlyDup && list.length > 40 && <button type="button" className="bfa-link" onClick={() => setAll(true)} data-testid="bfa-show-all-products">Arată toate cele {list.length} produse (sau caută mai sus)</button>}
        <button type="button" className="st-btn st-btn--ghost" onClick={add} data-testid="bfa-add-product"><Plus size={14} /> Adaugă produs</button>
        <h3>Pachete {cfg.brands.find((b) => b.id === tab)?.name}</h3>
        <BFPackageEditor brand={tab} cfg={cfg} setCfg={setCfg} />
      </section>}
      {is("produse") && <BFWaits />}
      {is("r_oferte") && <section className="invite-box">
        <h3>Card WhatsApp / Instagram Story (1080×1920)</h3>
        <p className="st-hint">Cardul folosește datele din pagină. Salvează înainte modificările ca să apară pe card.</p>
        <div className="share-actions">
          <button type="button" className="st-btn st-btn--silver" disabled={story !== undefined} onClick={() => exportStory(tab)} data-testid="bfa-story-brand">
            {story !== undefined ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />} Card {cfg.brands.find((b) => b.id === tab)?.name}
          </button>
          <button type="button" className="st-btn st-btn--ghost" disabled={story !== undefined} onClick={() => exportStory(null)} data-testid="bfa-story-all">
            <ImageIcon size={16} /> Card toate brandurile
          </button>
        </div>
        {brandTabs}
      </section>}
      {story !== undefined && <div className="bfs-stage"><BFStoryCard ref={storyRef} cfg={cfg} brand={story} /></div>}
      </main>
    </div>
  );
}
